import
{
    IonButton,
    IonCol,
    IonGrid,
    IonIcon,
    IonInput,
    IonItem,
    IonRow
} from "@ionic/react";

import
{
    SwiperSlide,
    useSwiper
} from 'swiper/react';

import
{
    LicenseType,
    RTMDisplay,
    SystemRTM,
    SystemType,
    UserEventDisplay
} from "@ews/react-data";

import React, {
    useCallback,
    useEffect,
    useRef,
    useState
} from "react";

import
{
    useIsMobile
} from "../../Responsive";

import
{
    EventItem,
    LastAlarmView
} from "../EventItem/EventItem";

import
{
    distributeRTMFields
} from "../helper.ts/distributeRTMFields";

import
{
    EventObject,
    EventTranslation,
    EventType,
    RTMFieldOrder,
    SortRTMFieldOrder
} from "../types";

import
{
    NumberGroup,
    RTMCategory
} from "@ews/zlt-events";

import
{
    useL10n
} from "@ews/react-localization-context";

import
{
    useMyEventDisplay
} from "../../Login/LoginProvider";

import { reload } from "ionicons/icons";
import type { FilterProps } from "../../../pages/System";
import { MAX_DEVICE_NUMBER } from "../../../utils";
import { useCurrentSystemId } from "../../Authorization";
import FilterList, { isFilterActive } from "../../FilterList/FilterList";
import { FilterState } from "../../FilterList/types";
import { zoneCompare } from "../../Group/GroupOverview/helper";
import { FilterCategories, FilterGroups } from "../../Group/types";
import { useHasLicense } from "../../HasLicense";
import { useNamedRoutes } from "../../NamedRoutes";
import { usePersistentState } from "../../Persistence";
import
{
    useSystem
} from "../../ReactData/system";
import SettingsSwiper from "../../Settings/SettingsComponents/SettingsSwiper/SettingsSwiper";
import SettingsTextInput from "../../Settings/SettingsComponents/SettingsTextInput/SettingsTextInput";
import { SwiperRedirectPageOneListener } from "../../SwiperRedirectPageOneListener/SwiperRedirectPageOneListener";
import EventCardButton from "../EventCardButton/EventCardButton";
import eventStyle from './EventOverview.module.scss';


export function rtmSortOrder(categoryTypeA: EventType, categoryTypeB: EventType)
{
    let posA = SortRTMFieldOrder[categoryTypeA];
    let posB = SortRTMFieldOrder[categoryTypeB];

    return posA - posB;
};

type RangeFilter = FilterGroups & FilterState;

const standardFilter: Record<string | number, any> = {};
FilterCategories.map(category => standardFilter[category] = {});
const ignoreKeys: (keyof RangeFilter)[] = ["isFilterOpen"];

const EventOverview: React.FC<{
    eventTab?: RTMDisplay,
    rtm: SystemRTM[];
} & FilterProps> = ({
    eventTab = RTMDisplay.eventsTab1,
    rtm,
    filter,
    onIsOpen,
    onFilterActive
}) =>
    {
        const { params, generate } = useNamedRoutes();

        const { page } = params();

        const systemId = useCurrentSystemId();
        const system = useSystem(systemId);

        const eventDisplay = useMyEventDisplay();
        const { translate: t } = useL10n();
        const mobileView = useIsMobile();
        const [rangeFilter, setRangeFilter] = usePersistentState<FilterGroups & FilterState>('system-event:filter', standardFilter, systemId);

        const slideToRef = useRef<number>(0);
        const swiperInputRef = useRef<HTMLIonInputElement>(null);

        const hasDetailView = useHasLicense(LicenseType.DETAIL_VIEW);

        const [currentPage, setCurrentPage] = useState<number>(page ? Number(page) : 0);


        const onIsFilterOpen = useCallback((state: boolean) => { onIsOpen && onIsOpen(state, "main"); }, [onIsOpen]);
        const onIsDetailFilterOpen = useCallback((isDetailOpen: boolean) => onIsOpen && onIsOpen(isDetailOpen, "detail"), [onIsOpen]);
        const onResetFilter = useCallback(() =>
        {
            setRangeFilter(() => standardFilter);
        }, [setRangeFilter]);

        const isFilterChanged = isFilterActive<FilterGroups & FilterState>(rangeFilter, standardFilter, ignoreKeys);

        useEffect(() =>
        {
            if (filter?.isFilterActive !== isFilterChanged) {
                onFilterActive && onFilterActive(isFilterChanged);
            }
        }, [isFilterChanged, filter, onFilterActive]);


        useEffect(() =>
        {
            if (filter?.isFilterActive !== isFilterChanged) {
                onFilterActive && onFilterActive(isFilterChanged);
            }
        }, [isFilterChanged, filter, onFilterActive]);

        const rtmFields = distributeRTMFields(rtm.filter(e =>
        {
            const { numberGroup } = e;
            const filter = rangeFilter[numberGroup as keyof FilterGroups];

            if (filter) {

                if (numberGroup === NumberGroup.FloodingZone) {
                    return zoneCompare(e.element, parseInt(filter.zoneFrom || "1")) >= 0 &&
                        zoneCompare(e.element, parseInt(filter.zoneTo || `${MAX_DEVICE_NUMBER}`)) <= 0;
                }

                return zoneCompare(e.zone, parseInt(filter.zoneFrom || "1")) >= 0 &&
                    zoneCompare(e.zone, parseInt(filter.zoneTo || `${MAX_DEVICE_NUMBER}`)) <= 0;
            }

            return true;
        }));

        const SwiperButtonNext: React.FC<{ swipeRef: any; }> = ({ swipeRef }) =>
        {
            const swiper = useSwiper();

            return <IonInput
                label=""
                ref={swipeRef}
                onClick={(e) =>
                {
                    swiper.slideToLoop(slideToRef.current);
                }}
                hidden />;
        };

        const swiperOption = {
            spaceBetween: 40,
            slidesPerView: 1,
            duration: '2s',
            animation: 'ease-in-out'
        };



        if (!("tabCategoryAlarm" in eventDisplay)) return <></>;

        rtmFields[RTMCategory.None] = null;

        if (!system.hasFloodingZone) {
            rtmFields[RTMCategory.ExtinguishingEvent] = null;
        }

        if (system.systemType === SystemType.BC216) {
            rtmFields[RTMCategory.Confirmation] = null;

        } else if (!mobileView) {
            for (const rtmField of RTMFieldOrder) {
                if (eventDisplay[`tabCategory${rtmField}` as keyof UserEventDisplay] !== eventTab) {
                    rtmFields[RTMCategory[rtmField]] = null;
                }
            }
        }

        const SwiperComponent = mobileView ? SettingsSwiper : IonGrid;
        const SliderComponent = mobileView ? SwiperSlide : ({ children }: { children: React.ReactNode; }) => <>{children}</>;
        const ColComponent = mobileView ? ({ children }: { children: React.ReactNode; }) => <>{children}</> : IonCol;
        const RowComponent = mobileView ? ({ children }: { children: React.ReactNode; }) => <>{children}</> : IonRow;
        let secondPage: EventObject[] | [] = [];

        //if (eventList.length > 6 && !mobileView) secondPage = eventList.splice(6, eventList.length - 1)

        return <>{/* <IonContent className={eventStyle["event-card-content-container"]} style={mobileView ? { height: '85%' } : {}}>{} */}
            {/*  {secondPage.length && !mobileView ? <SettingsSegment value={currentPage} onSelect={(current) => setCurrentPage(current)}>
            <IonSegmentButton value="page 1">{'Event 1'}</IonSegmentButton>
            <IonSegmentButton value="page 2">{'Event 2'}</IonSegmentButton>
        </SettingsSegment> : null} */}

            {
                <div hidden={mobileView ? true : !filter?.isFilterOpen} className={mobileView ? eventStyle.filterContainer : ""}>
                    <SwiperRedirectPageOneListener
                        swiperInputRef={swiperInputRef}
                        slideToRef={slideToRef}
                        page={currentPage}
                        link={'system:events'}
                    />
                    <FilterList
                        isOpen={filter?.isFilterOpen || false}
                        isDetailsOpen={filter?.isDetailOpen || false}
                        onIsOpen={onIsFilterOpen}
                        onDetailOpen={onIsDetailFilterOpen}
                        onReset={onResetFilter}
                    >

                        {FilterCategories.map((key) =>
                        {
                            rangeFilter[key as keyof FilterGroups] = rangeFilter[key as keyof FilterGroups] || {};

                            if (key === NumberGroup.FloodingZone && !system.hasFloodingZone) {
                                return <React.Fragment key={key} />;
                            }

                            return <IonCol key={key} >
                                <IonItem>
                                    <div style={{ width: "10em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t(`[REACT_GROUP_RANGE_PICKER_${key}]`)}</div>
                                    <SettingsTextInput
                                        lines="none"
                                        text=""
                                        className={
                                            eventStyle["remove-arrows"]
                                        }
                                        textAlign="left"
                                        inputType="number"
                                        value={`${rangeFilter[key as unknown as keyof FilterGroups]?.zoneFrom || 1}`}
                                        onChange={(value) =>
                                        {
                                            rangeFilter[key as unknown as keyof FilterGroups]!.zoneFrom = value;
                                            setRangeFilter({ ...rangeFilter });
                                        }}
                                    /> -
                                    <SettingsTextInput
                                        lines="none"
                                        text=""
                                        className={
                                            eventStyle["remove-arrows"]
                                        }
                                        textAlign="left"
                                        inputType="number"
                                        value={`${rangeFilter[key as unknown as keyof FilterGroups]?.zoneTo || MAX_DEVICE_NUMBER}`}
                                        onChange={(value) =>
                                        {
                                            rangeFilter[key as unknown as keyof FilterGroups]!.zoneTo = value;
                                            setRangeFilter({ ...rangeFilter });
                                        }}
                                    />
                                    <IonButton
                                        onClick={() =>
                                        {
                                            rangeFilter[key as unknown as keyof FilterGroups] = {};
                                            setRangeFilter({ ...rangeFilter });
                                        }}
                                    ><IonIcon icon={reload} /></IonButton>
                                </IonItem>
                            </IonCol>;
                        })}
                    </FilterList>
                </div>
            }

            <SwiperComponent
                className={`${!mobileView && filter?.isFilterOpen ? eventStyle["filter"] : ""} ${eventStyle["event-swiper"]}`}
                spaceBetween={swiperOption.spaceBetween}
                slidesPerView={1}
                value={currentPage}
                loop
                onSlideChange={(value) => setCurrentPage(value)}
            >
                {mobileView && <SwiperButtonNext swipeRef={swiperInputRef} />}
                {mobileView ? <SliderComponent key={'event-overview-slider-main'} >
                    <IonGrid className={mobileView ? eventStyle['event-overview-grid'] : ''} >
                        <IonRow >
                            {RTMFieldOrder
                                .sort(rtmSortOrder)
                                .map((categoryType, index) =>
                                {
                                    const category: RTMCategory = RTMCategory[categoryType];
                                    const rtm = rtmFields[category];

                                    let swiperIndex = SortRTMFieldOrder[categoryType];

                                    const hasConfirmation = system.systemType === SystemType.BC216 && (SortRTMFieldOrder[categoryType] > SortRTMFieldOrder.Confirmation);

                                    if ((!system.hasFloodingZone && categoryType !== "Alarm") || hasConfirmation) {
                                        swiperIndex -= hasConfirmation ? 2 : 1;
                                    }

                                    if (!rtm) return <React.Fragment key={categoryType} />;

                                    return <IonCol size="4" key={categoryType} className={eventStyle["event-card-button"]} >
                                        <EventCardButton
                                            count={rtm.length}
                                            swiperIndex={swiperIndex}
                                            title={t(`[REACT_FC_${categoryType.toUpperCase()}]`)}
                                            type={categoryType}
                                            onClick={(page) =>
                                            {
                                                //slideToRef.current = page;
                                                slideToRef.current = swiperIndex;
                                                swiperInputRef.current?.click();
                                            }} />
                                    </IonCol>;
                                })}
                        </IonRow>
                    </IonGrid>
                </SliderComponent> : null}

                {hasDetailView ? <>
                    <RowComponent className={eventStyle["event-row"]} >
                        <>
                            {RTMFieldOrder
                                .sort(rtmSortOrder)
                                .map((categoryType) =>
                                {
                                    const category: RTMCategory = RTMCategory[categoryType];
                                    const rtm = rtmFields[category];

                                    if (!rtm) return <React.Fragment key={categoryType} />;

                                    const isAlarmCategory = category === RTMCategory.Alarm;
                                    const hasAlarm = isAlarmCategory && rtm.length;

                                    return <SliderComponent key={categoryType} className={eventStyle['event-swiper']}>
                                        <ColComponent size="4">
                                            <EventItem
                                                rtm={rtm}
                                                className={hasAlarm ? eventStyle["event-card-alarm-item"] : ""}
                                                title={EventTranslation[categoryType]}
                                            >
                                                {hasAlarm ? <LastAlarmView rtm={rtm} /> : null}
                                            </EventItem>
                                        </ColComponent>
                                    </SliderComponent>;
                                })}
                        </>
                    </RowComponent>
                </> : null}
            </SwiperComponent >

            {
                secondPage.length ? <RowComponent>
                    <></>
                    {/*  {secondPage.map((event: EventObject, index: number) =>
            {

                return <SliderComponent key={'event-overview-slider-' + index} className={eventStyle['event-swiper']}>

                    <ColComponent size="4">
                        <EventItem event={event} />
                    </ColComponent>
                </SliderComponent>;

            })} */}
                </RowComponent> : null
            }

            {/* </IonContent> */}
        </>;
    };
export default EventOverview;
